import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Badge } from '../../components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import { 
  Route, 
  Plus, 
  Edit, 
  Trash2, 
  Play, 
  Pause, 
  Settings,
  Zap,
  Users,
  Tag,
  Clock,
  AlertTriangle,
  CheckCircle,
  BarChart3,
  Filter,
  Search,
  Save,
  RotateCcw,
  Eye,
  TestTube,
  Brain,
  Target,
  ArrowRight,
  ArrowDown,
  ArrowUp
} from 'lucide-react';
import { useDataContext } from '../../contexts/DataContext';

const RoutingRulesConfiguration = () => {
  const { getMockData, updateMockData } = useDataContext();
  const [routingRules, setRoutingRules] = useState([]);
  const [agents, setAgents] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isCreatingRule, setIsCreatingRule] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadRoutingData();
  }, []);

  const loadRoutingData = async () => {
    try {
      const data = getMockData('routingRules');
      const agentsData = getMockData('agents');
      const departmentsData = getMockData('departments');
      const categoriesData = getMockData('categories');
      
      setRoutingRules(data?.routingRules || []);
      setAgents(agentsData?.agents || []);
      setDepartments(departmentsData?.departments || []);
      setCategories(categoriesData?.categories || []);
    } catch (error) {
      console.error('Failed to load routing data:', error);
    }
  };

  const handleCreateRule = () => {
    setIsCreatingRule(true);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
  };

  const handleDeleteRule = (ruleId) => {
    if (window.confirm('Are you sure you want to delete this routing rule?')) {
      const updatedRules = routingRules.filter(rule => rule.id !== ruleId);
      setRoutingRules(updatedRules);
      updateMockData('routingRules', updatedRules);
    }
  };

  const handleToggleRule = (ruleId) => {
    const updatedRules = routingRules.map(rule => 
      rule.id === ruleId ? { ...rule, isActive: !rule.isActive } : rule
    );
    setRoutingRules(updatedRules);
    updateMockData('routingRules', updatedRules);
  };

  const handleTestRule = (rule) => {
    alert(`Testing rule: ${rule.name}\nThis would route tickets matching: ${rule.conditions.map(c => c.description).join(', ')}`);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'green';
      case 'inactive': return 'gray';
      case 'testing': return 'yellow';
      default: return 'gray';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return CheckCircle;
      case 'inactive': return Pause;
      case 'testing': return TestTube;
      default: return AlertTriangle;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'red';
      case 'medium': return 'yellow';
      case 'low': return 'green';
      default: return 'gray';
    }
  };

  const filteredRules = routingRules.filter(rule => {
    const matchesSearch = rule.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         rule.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || rule.isActive === (filterStatus === 'active');
    return matchesSearch && matchesStatus;
  });

  const mockRoutingRules = [
    {
      id: 1,
      name: 'High Priority Technical Issues',
      description: 'Route high priority technical issues to senior technical agents',
      isActive: true,
      priority: 'high',
      conditions: [
        { type: 'priority', operator: 'equals', value: 'high', description: 'Priority is High' },
        { type: 'category', operator: 'equals', value: 'technical', description: 'Category is Technical' }
      ],
      actions: [
        { type: 'assign_to_department', value: 'technical', description: 'Assign to Technical Department' },
        { type: 'set_priority', value: 'high', description: 'Set Priority to High' },
        { type: 'add_tag', value: 'urgent', description: 'Add Urgent Tag' }
      ],
      targetAgent: null,
      targetDepartment: 'Technical Support',
      successRate: 95.2,
      lastUsed: '2025-01-15T10:30:00Z',
      usageCount: 1247,
      createdAt: '2024-12-01T00:00:00Z'
    },
    {
      id: 2,
      name: 'Billing Questions',
      description: 'Route billing-related tickets to billing specialists',
      isActive: true,
      priority: 'medium',
      conditions: [
        { type: 'category', operator: 'equals', value: 'billing', description: 'Category is Billing' },
        { type: 'keywords', operator: 'contains', value: 'invoice,payment,subscription', description: 'Contains billing keywords' }
      ],
      actions: [
        { type: 'assign_to_department', value: 'billing', description: 'Assign to Billing Department' },
        { type: 'set_sla', value: '24h', description: 'Set SLA to 24 hours' }
      ],
      targetAgent: null,
      targetDepartment: 'Billing',
      successRate: 98.7,
      lastUsed: '2025-01-15T09:45:00Z',
      usageCount: 892,
      createdAt: '2024-11-15T00:00:00Z'
    },
    {
      id: 3,
      name: 'VIP Customer Support',
      description: 'Route VIP customer tickets to dedicated agents',
      isActive: true,
      priority: 'high',
      conditions: [
        { type: 'customer_tier', operator: 'equals', value: 'vip', description: 'Customer is VIP' },
        { type: 'priority', operator: 'in', value: 'high,medium', description: 'Priority is High or Medium' }
      ],
      actions: [
        { type: 'assign_to_agent', value: 'sarah.johnson', description: 'Assign to Sarah Johnson' },
        { type: 'set_priority', value: 'high', description: 'Set Priority to High' },
        { type: 'add_tag', value: 'vip', description: 'Add VIP Tag' }
      ],
      targetAgent: 'Sarah Johnson',
      targetDepartment: null,
      successRate: 99.1,
      lastUsed: '2025-01-15T08:20:00Z',
      usageCount: 156,
      createdAt: '2024-10-01T00:00:00Z'
    },
    {
      id: 4,
      name: 'Integration Issues',
      description: 'Route integration-related tickets to integration specialists',
      isActive: false,
      priority: 'medium',
      conditions: [
        { type: 'category', operator: 'equals', value: 'integration', description: 'Category is Integration' },
        { type: 'keywords', operator: 'contains', value: 'api,webhook,connector', description: 'Contains integration keywords' }
      ],
      actions: [
        { type: 'assign_to_department', value: 'integration', description: 'Assign to Integration Department' },
        { type: 'set_sla', value: '48h', description: 'Set SLA to 48 hours' }
      ],
      targetAgent: null,
      targetDepartment: 'Integration',
      successRate: 87.3,
      lastUsed: '2025-01-10T14:30:00Z',
      usageCount: 234,
      createdAt: '2024-09-15T00:00:00Z'
    },
    {
      id: 5,
      name: 'New Customer Onboarding',
      description: 'Route new customer tickets to onboarding specialists',
      isActive: true,
      priority: 'medium',
      conditions: [
        { type: 'customer_age', operator: 'less_than', value: '30', description: 'Customer account age < 30 days' },
        { type: 'category', operator: 'equals', value: 'onboarding', description: 'Category is Onboarding' }
      ],
      actions: [
        { type: 'assign_to_department', value: 'onboarding', description: 'Assign to Onboarding Department' },
        { type: 'set_priority', value: 'medium', description: 'Set Priority to Medium' },
        { type: 'add_tag', value: 'new-customer', description: 'Add New Customer Tag' }
      ],
      targetAgent: null,
      targetDepartment: 'Customer Success',
      successRate: 92.8,
      lastUsed: '2025-01-15T07:15:00Z',
      usageCount: 445,
      createdAt: '2024-08-01T00:00:00Z'
    }
  ];

  const routingMetrics = [
    {
      title: 'Total Rules',
      value: routingRules.length,
      icon: Route,
      color: 'blue',
      trend: '+2',
      trendDirection: 'up'
    },
    {
      title: 'Active Rules',
      value: routingRules.filter(r => r.isActive).length,
      icon: CheckCircle,
      color: 'green',
      trend: '+1',
      trendDirection: 'up'
    },
    {
      title: 'Success Rate',
      value: '94.2%',
      icon: Target,
      color: 'green',
      trend: '+1.2%',
      trendDirection: 'up'
    },
    {
      title: 'Tickets Routed Today',
      value: 1247,
      icon: ArrowRight,
      color: 'blue',
      trend: '+15%',
      trendDirection: 'up'
    }
  ];

  return (
    <div className="routing-rules-configuration p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Routing Rules Configuration</h1>
            <p className="text-gray-600">Configure automated ticket routing rules and AI-powered suggestions</p>
          </div>
          <div className="flex items-center space-x-4">
            <Button onClick={handleCreateRule}>
              <Plus className="h-4 w-4 mr-2" />
              Create Rule
            </Button>
            <Button variant="outline">
              <Brain className="h-4 w-4 mr-2" />
              AI Suggestions
            </Button>
          </div>
        </div>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {routingMetrics.map((metric, index) => {
          const Icon = metric.icon;
          return (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium text-gray-600">{metric.title}</CardTitle>
                <Icon className={`h-4 w-4 text-${metric.color}-500`} />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-gray-900">{metric.value}</div>
                <div className={`flex items-center text-xs ${
                  metric.trendDirection === 'up' ? 'text-green-600' : 'text-red-600'
                }`}>
                  <ArrowUp className="h-3 w-3 mr-1" />
                  {metric.trend} from yesterday
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Filters and Search */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Filter className="h-5 w-5 mr-2" />
            Filter Rules
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Search rules..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Rules</SelectItem>
                <SelectItem value="active">Active Only</SelectItem>
                <SelectItem value="inactive">Inactive Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Routing Rules List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <Route className="h-5 w-5 mr-2" />
            Routing Rules ({filteredRules.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {mockRoutingRules.map((rule) => {
              const StatusIcon = getStatusIcon(rule.isActive ? 'active' : 'inactive');
              return (
                <div key={rule.id} className="p-6 border rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-semibold">{rule.name}</h3>
                        <Badge variant={rule.isActive ? 'default' : 'secondary'}>
                          <StatusIcon className="h-3 w-3 mr-1" />
                          {rule.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                        <Badge variant={getPriorityColor(rule.priority) === 'red' ? 'destructive' : 'default'}>
                          {rule.priority} priority
                        </Badge>
                      </div>
                      <p className="text-gray-600 mb-3">{rule.description}</p>
                      
                      {/* Conditions */}
                      <div className="mb-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Conditions:</h4>
                        <div className="flex flex-wrap gap-2">
                          {rule.conditions.map((condition, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {condition.description}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="mb-3">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Actions:</h4>
                        <div className="flex flex-wrap gap-2">
                          {rule.actions.map((action, index) => (
                            <Badge key={index} variant="outline" className="text-xs">
                              {action.description}
                            </Badge>
                          ))}
                        </div>
                      </div>

                      {/* Target Assignment */}
                      <div className="flex items-center space-x-4 text-sm text-gray-600">
                        {rule.targetAgent && (
                          <div className="flex items-center">
                            <Users className="h-4 w-4 mr-1" />
                            <span>Agent: {rule.targetAgent}</span>
                          </div>
                        )}
                        {rule.targetDepartment && (
                          <div className="flex items-center">
                            <Tag className="h-4 w-4 mr-1" />
                            <span>Department: {rule.targetDepartment}</span>
                          </div>
                        )}
                        <div className="flex items-center">
                          <BarChart3 className="h-4 w-4 mr-1" />
                          <span>Success Rate: {rule.successRate}%</span>
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          <span>Used {rule.usageCount} times</span>
                        </div>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center space-x-2 ml-4">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleTestRule(rule)}
                      >
                        <TestTube className="h-3 w-3 mr-1" />
                        Test
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEditRule(rule)}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleToggleRule(rule.id)}
                      >
                        {rule.isActive ? (
                          <>
                            <Pause className="h-3 w-3 mr-1" />
                            Disable
                          </>
                        ) : (
                          <>
                            <Play className="h-3 w-3 mr-1" />
                            Enable
                          </>
                        )}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteRule(rule.id)}
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* AI Suggestions */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle className="flex items-center">
            <Brain className="h-5 w-5 mr-2" />
            AI-Powered Suggestions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <Brain className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-blue-900">Suggested Rule: Escalation for Long-Running Tickets</h4>
                  <p className="text-blue-700 text-sm mt-1">
                    Based on your ticket data, we suggest creating a rule to automatically escalate tickets that have been open for more than 48 hours.
                  </p>
                  <div className="flex items-center space-x-2 mt-2">
                    <Button size="sm" variant="outline">
                      <Plus className="h-3 w-3 mr-1" />
                      Create Rule
                    </Button>
                    <Button size="sm" variant="outline">
                      <Eye className="h-3 w-3 mr-1" />
                      View Details
                    </Button>
                  </div>
                </div>
              </div>
            </div>

            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-start space-x-3">
                <Zap className="h-5 w-5 text-green-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-green-900">Optimization: Merge Similar Rules</h4>
                  <p className="text-green-700 text-sm mt-1">
                    We found 3 rules with similar conditions that could be merged to improve efficiency and reduce conflicts.
                  </p>
                  <div className="flex items-center space-x-2 mt-2">
                    <Button size="sm" variant="outline">
                      <Settings className="h-3 w-3 mr-1" />
                      Optimize
                    </Button>
                    <Button size="sm" variant="outline">
                      <Eye className="h-3 w-3 mr-1" />
                      Review
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RoutingRulesConfiguration;
